'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { themeConfig } from '@/config/theme.config';
import { config } from '@/config';

/**
 * Theme initialization state
 */
interface ThemeState {
  isInitialized: boolean;
  themeName: string;
  theme: Record<string, string>;
  error: string | null;
}

/**
 * Custom hook for dynamic theme initialization
 * Ensures theme is applied before any template rendering
 */
export const useThemeInitializer = () => {
  const [themeState, setThemeState] = useState<ThemeState>({
    isInitialized: false,
    themeName: '',
    theme: {},
    error: null,
  });

  /**
   * Apply theme to CSS custom properties
   */
  const applyTheme = useCallback((themeName: string) => {
    const theme = themeConfig[themeName as keyof typeof themeConfig] || themeConfig.flysync;
    
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      
      // Apply all theme colors as CSS custom properties
      Object.entries(theme).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
      
      // Add theme class to body for additional styling
      document.body.className = document.body.className
        .replace(/theme-\w+/g, '')
        .trim() + ` theme-${themeName}`;
    }
    
    return theme;
  }, []);

  /**
   * Get theme name from corporate name
   */
  const getThemeNameFromCorporate = useCallback((corporateName: string): string => {
    // Direct mapping: corporateName -> themeName
    const themeName = corporateName || 'flysync';
    
    // Validate theme exists
    if (!themeConfig[themeName as keyof typeof themeConfig]) {
      console.warn(`Theme '${themeName}' not found, falling back to 'flysync'`);
      return 'flysync';
    }
    
    return themeName;
  }, []);

  /**
   * Initialize theme system
   */
  const initializeTheme = useCallback(async () => {
    try {
      setThemeState(prev => ({ ...prev, error: null }));
      
      // Get corporate name from config
      const corporateName = config.corporateName || 'flysync';
      
      // Map corporate name to theme name
      const themeName = getThemeNameFromCorporate(corporateName);
      
      // Apply theme
      const theme = applyTheme(themeName);
      
      // Update state
      setThemeState({
        isInitialized: true,
        themeName,
        theme,
        error: null,
      });
      
      console.log(`🎨 Theme initialized: ${themeName} for corporate: ${corporateName}`);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown theme initialization error';
      console.error('Theme initialization failed:', errorMessage);
      
      setThemeState(prev => ({
        ...prev,
        error: errorMessage,
        isInitialized: false,
      }));
    }
  }, [applyTheme, getThemeNameFromCorporate]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  /**
   * Re-initialize theme (useful for dynamic theme switching)
   */
  const reinitializeTheme = useCallback((newCorporateName?: string) => {
    if (newCorporateName) {
      // Temporarily update config for this initialization
      const originalCorporateName = config.corporateName;
      config.corporateName = newCorporateName;
      
      initializeTheme().finally(() => {
        // Restore original config
        config.corporateName = originalCorporateName;
      });
    } else {
      initializeTheme();
    }
  }, [initializeTheme]);

  return {
    ...themeState,
    reinitializeTheme,
    applyTheme,
  };
};

/**
 * Higher-order component for theme initialization
 */
export function withThemeInitializer<P extends object>(
  Component: React.ComponentType<P>
) {
  return function ThemedComponent(props: P) {
    const { isInitialized, error } = useThemeInitializer();
    
    if (error) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-red-50">
          <div className="text-center p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold text-red-600 mb-2">Theme Error</h2>
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      );
    }
    
    if (!isInitialized) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Initializing theme...</p>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
}
